import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  MenuItem,
  Paper
} from "@mui/material";
import {
  NavigateNext,
  NavigateBefore,
  Check,
  Save,
  Person,
  Home,
  Language,
  Public
} from "@mui/icons-material";
import Header from "./Header";
import { useLanguage } from '../context/LanguageContext';
import { styled } from '@mui/material/styles';
import StepConnector from '@mui/material/StepConnector';

const StepIconWithDirection = ({ icon, completed, isRtl }) => {
  const IconComponent = completed ? Check : icon?.type;

  if (!IconComponent) return null;

  return (
    <IconComponent sx={{
      fontSize: 24,
      transform: isRtl ? 'scaleX(-1)' : 'none'
    }} />
  );
};

const CustomConnector = styled(StepConnector)(({ theme, isRtl }) => ({
  '&.MuiStepConnector-root': {
    left: 'calc(-50% + 20px)',
    right: 'calc(50% + 20px)',
    [theme.breakpoints.down('sm')]: {
      left: 'calc(-50% + 16px)',
      right: 'calc(50% + 16px)',
    },
  },
  '&.MuiStepConnector-alternativeLabel': {
    top: 12,
  },
  '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[600],
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const RTLAwareConnector = (props) => {
  const { isRtl, activeStep, index } = props;

  // For RTL, we need to invert the active/completed logic for connectors
  let isActive, isCompleted;

  if (isRtl) {
    // In RTL mode, connectors are active/completed in reverse order
    const totalSteps = 3; // Total number of steps
    const rtlIndex = totalSteps - 1 - index;
    isActive = rtlIndex === activeStep;
    isCompleted = rtlIndex < activeStep;
  } else {
    // Standard LTR logic
    isActive = index === activeStep;
    isCompleted = index < activeStep;
  }

  return (
    <CustomConnector
      {...props}
      isRtl={isRtl}
      className={`${props.className} ${isActive ? 'Mui-active' : ''} ${isCompleted ? 'Mui-completed' : ''}`}
    />
  );
};

const DirectionAwareIcon = ({ icon, isRtl }) => {
  const IconComponent = icon.type;
  return <IconComponent sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />;
};

const ProfileSetup = ({ userEmail, fetchUserData, onProfileUpdated, isEditing, onSignOut, onCancel }) => {
  const API_URL = "https://me40bh7o7b.execute-api.us-east-1.amazonaws.com/prod/user";
  const {
    currentLanguage,
    changeLanguage,
    t,
    supportedLanguages,
    getLanguageName,
    isRtl,
    direction
  } = useLanguage();
  const currentLangMeta = supportedLanguages.find(lang => lang.code === currentLanguage);
  console.log("Lang Direction:", direction);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  const theme = {
    primary: "#E86C40",
    secondary: "#1EAEB8",
    text: "#3D4760",
    background: "#f5f5f5"
  };

  const renderLanguageModal = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        overflow: 'auto'
      }}
      onClick={() => setShowLanguageModal(false)} // Add this to close when clicking outside
    >
      <Card
        sx={{
          width: '90%',
          maxWidth: 400,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside card from closing
      >
        <CardHeader
          title={t('selectLanguage')}
          sx={{
            backgroundColor: theme.primary,
            color: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}
        />
        <CardContent sx={{
          overflow: 'auto',
          flex: 1,
          p: 2
        }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('chooseLanguage')}
          </Typography>
          <TextField
            select
            fullWidth
            value={currentLanguage}
            onChange={(e) => {
              changeLanguage(e.target.value);
              setShowLanguageModal(false);
            }}
            label={t('language')}
          >
            {supportedLanguages.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>
    </Box>
  );

  // State Variables
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [roleType, setRoleType] = useState("");
  const [acknowledgment, setAcknowledgment] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [dwellingType, setDwellingType] = useState("house");
  const [apartmentName, setApartmentName] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (userEmail) {
      fetchUserProfile();
    }
  }, [userEmail]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API_URL}?user_email=${encodeURIComponent(userEmail)}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error(`Error fetching user profile: ${response.statusText}`);

      const data = await response.json();
      console.log("🟢 Fetched User Profile Data:", data);

      if (data.user) {
        setFirstName(data.user.first_name || "");
        setLastName(data.user.last_name || "");
        setRoleType(data.user.role_type || "");
        setAcknowledgment(data.user.acknowledgment || false);
        setPhoneNumber(data.user.phone_number || "");
        setStreetAddress(data.user.street_address || "");
        setCity(data.user.city || "");
        setZipCode(data.user.zip_code || "");
        setDwellingType(data.user.dwelling_type || "house");
        setApartmentName(data.user.apartment_name || "");
        setApartmentNumber(data.user.apartment_number || "");
        setPreferredLanguage(data.user.preferred_language || "");
      }
    } catch (error) {
      console.error("🚨 Error fetching user profile:", error);
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (loading) return;

    if (!isEditing && !acknowledgment) {
      alert("You must acknowledge the terms to proceed.");
      return;
    }

    setLoading(true);

    const profileData = {
      user_email: userEmail,
      first_name: firstName,
      last_name: lastName,
      role_type: roleType,
      acknowledgment: acknowledgment,
      phone_number: phoneNumber,
      street_address: streetAddress,
      city: city,
      zip_code: zipCode,
      dwelling_type: dwellingType,
      apartment_name: apartmentName,
      apartment_number: apartmentNumber,
      preferred_language: preferredLanguage
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error updating profile: ${errorText}`);
      }

      //await response.json();
      await fetchUserData();
      onProfileUpdated();
    } catch (error) {
      console.error("🚨 Error updating profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Steps configuration for multi-step form
  const steps = [
    { label: 'personalInfoStep', icon: <Person /> },
    { label: 'housingInfoStep', icon: <Home /> },
    { label: 'languageStep', icon: <Language /> }
  ];

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Form validation by step
  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return firstName && lastName && phoneNumber;
      case 1:
        return streetAddress && city && zipCode;
      case 2:
        return preferredLanguage && acknowledgment;
      default:
        return true;
    }
  };

  const orderedSteps = isRtl ? [...steps].reverse() : steps;

  // Flip active step for RTL
  const stepperActiveStep = isRtl ? steps.length - 1 - activeStep : activeStep;

  const visualActiveIndex = isRtl
    ? steps.length - 1 - activeStep
    : activeStep;

  // Recommended languages based on common refugee populations
  const commonLanguages = [
    "Arabic",
    "Burmese",
    "Dari",
    "English",
    "Farsi",
    "French",
    "Kinyarwanda",
    "Kurdish",
    "Pashto",
    "Somali",
    "Spanish",
    "Swahili",
    "Tigrinya"
  ];

  // Form content by step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography variant="h6" color={theme.secondary} gutterBottom>
              {t('welcome')}
            </Typography>
            <TextField
              fullWidth
              label={t('firstName')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label={t('lastName')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              select
              fullWidth
              label={t('roleLabel')}
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              sx={{ mb: 3 }}
            >
              <MenuItem value="Volunteer">{t('volunteer')}</MenuItem>
              {/*<MenuItem value="refugee">{t('refugee')}</MenuItem>*/}
              <MenuItem value="Parent">{t('parent')}</MenuItem>
              <MenuItem value="Guardian">{t('guardian')}</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label={t('phone')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
          </>
        );
      case 1:
        return (
          <>
            <Typography variant="h6" color={theme.secondary} gutterBottom>
              {t('housing')}
            </Typography>
            <TextField
              fullWidth
              label={t('street')}
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label={t('city')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label={t('zip')}
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              sx={{ mb: 3 }}
              required
            />
            <TextField
              select
              fullWidth
              label={t('dwelling')}
              value={dwellingType}
              onChange={(e) => setDwellingType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="house">{t('house')}</MenuItem>
              <MenuItem value="apartment">{t('apartment')}</MenuItem>
            </TextField>
            {dwellingType === "apartment" && (
              <>
                <TextField
                  fullWidth
                  label={t('aptName')}
                  value={apartmentName}
                  onChange={(e) => setApartmentName(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('aptNumber')}
                  value={apartmentNumber}
                  onChange={(e) => setApartmentNumber(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </>
        );
      case 2:
        return (
          <>
            <Typography variant="h6" color={theme.secondary} gutterBottom>
              {t('languagePref')}
            </Typography>
            <TextField
              select
              fullWidth
              label={t('preferredLang')}
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value)}
              sx={{ mb: 4 }}
              required
            >
              {supportedLanguages.map((lang) => (
                <MenuItem key={lang.code} value={lang.name}>
                  {lang.name}
                </MenuItem>
              ))}
            </TextField>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(30, 174, 184, 0.1)', mb: 3, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 2, fontStyle: "italic", color: theme.text }}>
                {t('acknowledgment')}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledgment}
                    onChange={(e) => setAcknowledgment(e.target.checked)}
                    color="primary"
                    required
                  />
                }
                label={t('acknowledgeLabel')}
                sx={{ color: theme.text }}
              />
            </Paper>
          </>
        );
      default:
        return "Unknown step";
    }
  };

  const StepIconWithDirection = ({ icon, completed, isRtl }) => {
    const IconComponent = completed ? Check : icon?.type;

    if (!IconComponent) return null;

    return (
      <IconComponent sx={{
        fontSize: 24,
        // Only apply RTL transform if it's NOT a checkmark
        transform: (completed || icon?.type === Check) ? 'none' : (isRtl ? 'scaleX(-1)' : 'none')
      }} />
    );
  };

  const StepperWithCustomConnectors = ({ theme, isRtl, activeStep, steps, t }) => {
    const displaySteps = isRtl ? [...steps].reverse() : steps;

    return (
      <Box sx={{ width: '100%', padding: '24px 0' }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          direction: 'ltr', // Always LTR for visual display
          position: 'relative'
        }}>
          {displaySteps.map((step, displayIndex) => {
            const actualIndex = isRtl ? steps.length - 1 - displayIndex : displayIndex;
            const isCompleted = actualIndex < activeStep;
            const isActive = actualIndex === activeStep;

            return (
              <Box
                key={step.label}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  flex: 1,
                  zIndex: 2
                }}
              >
                {displayIndex > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: 'calc(-50% + 12px)',
                      right: 'calc(50% + 12px)',
                      height: 3,
                      bgcolor: (isRtl ? displayIndex >= steps.length - activeStep : displayIndex <= activeStep)
                        ? theme.primary
                        : '#e0e0e0'
                    }}
                  />
                )}

                <Box sx={{
                  bgcolor: isCompleted || isActive ? theme.primary : '#e0e0e0',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <StepIconWithDirection
                    icon={step.icon}
                    completed={isCompleted}
                    isRtl={isRtl}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    mt: 1,
                    textAlign: 'center',
                  }}
                >
                  {t(step.label)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.background,
      direction: isRtl ? 'rtl' : 'ltr'
    }}>
      {showLanguageModal && renderLanguageModal()}
      <Header
        showProfileIcon={false}
        onLanguageClick={() => setShowLanguageModal(true)}
        languageLabel={getLanguageName(currentLanguage)}
      />

      <Box sx={{
        flexGrow: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        p: 2,
        overflow: 'hidden',
        height: 'calc(100vh - 120px)' // Adjust based on header height
      }}>
        <Card sx={{
          width: "100%",
          maxWidth: 600,
          boxShadow: 3,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100%', // Take full available height
          overflow: 'hidden'
        }}>
          <CardHeader
            title={
              <Typography
                variant="h6"
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: "#fff"
                }}
              >
                <Person sx={{ mr: 1 }} />
                {isEditing ? t('editProfile') : t('completeProfile')}
              </Typography>
            }
            sx={{
              backgroundColor: theme.primary,
              color: "#fff",
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}
          />

          <CardContent sx={{
            p: 0, // Changed from p: 2 to control padding more precisely
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%'
          }}>
            {/* Fixed Stepper at top */}
            <Box sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'background.paper',
              zIndex: 2,
              px: 2,
              pt: 2,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
            <StepperWithCustomConnectors
              theme={theme}
              isRtl={isRtl}
              activeStep={activeStep}
              steps={steps}
              t={t}
            />
            </Box>

            {/* Scrollable form content */}
            <Box sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              pt: 2,
              pb: 2
            }}>
              <form>
                {getStepContent(activeStep)}
              </form>
            </Box>

            {/* Fixed buttons at bottom */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              pt: 2,
              pb: 2,
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                startIcon={<DirectionAwareIcon icon={<NavigateBefore />} isRtl={isRtl} />}
                disabled={activeStep === 0}
                sx={{
                  borderColor: theme.secondary,
                  color: theme.secondary,
                  '&:hover': {
                    borderColor: theme.primary,
                    color: theme.primary,
                    bgcolor: 'rgba(232, 108, 64, 0.1)'
                  }
                }}
              >
                {t('back')}
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={activeStep === steps.length - 1 ?
                  <DirectionAwareIcon icon={<Save />} isRtl={isRtl} /> :
                  <DirectionAwareIcon icon={<NavigateNext />} isRtl={isRtl} />}
                disabled={!isStepValid() || loading}
                sx={{
                  bgcolor: theme.primary,
                  color: "#fff",
                  '&:hover': {
                    bgcolor: '#c85936',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(232, 108, 64, 0.4)',
                    color: '#fff'
                  }
                }}
              >
                {activeStep === steps.length - 1 ? (loading ? t('saving') : t('complete')) : t('next')}
              </Button>
            </Box>

            {isEditing && (
              <>
                <Divider sx={{ my: 0 }} />
                <Grid container spacing={2} sx={{ px: 2, py: 2 }}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={onCancel}
                      sx={{
                        borderColor: theme.text,
                        color: theme.text,
                        '&:hover': {
                          borderColor: theme.primary,
                          color: theme.primary
                        }
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={onSignOut}
                    >
                      {t('signOut')}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{
        p: 2,
        bgcolor: theme.secondary,
        color: "#fff",
        textAlign: "center",
        position: 'sticky',
        bottom: 0
      }}>
        <Typography variant="body2">
          {t('footerText')}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProfileSetup;
